import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { createSchemaUtils, type RJSFSchema } from '@rjsf/utils'
import { Button } from '../../Button/Button'
import { applyMergePatch, buildMergePatch, isPlainObject, type JsonObject } from './mergePatch'
import { ParametersRjsfForm, type ParametersFormInstance } from './ParametersRjsfForm'
import { PARAMETERS_DEFAULT_FORM_STATE, parametersValidator } from './rjsf/validator'

export interface WorkflowParametersFormProps {
  schema: RJSFSchema
  baseline: Record<string, unknown>
  layer: Record<string, unknown> | null
  saving: boolean
  onSave: (patch: Record<string, unknown>) => Promise<boolean>
  onReset: () => Promise<boolean>
  onDirtyChange: (dirty: boolean) => void
  onUploadingChange?: (uploading: boolean) => void
  externallyUpdated: boolean
  onAcknowledgeExternalUpdate: () => void
  idPrefix?: string
  layerName: 'account' | 'user'
  onInvalid?: () => void
}

export interface WorkflowParametersFormHandle {
  validate: () => boolean
  save: () => Promise<boolean>
}

export const WorkflowParametersForm = forwardRef<WorkflowParametersFormHandle, WorkflowParametersFormProps>(function WorkflowParametersForm(
  {
    schema,
    baseline,
    layer,
    saving,
    onSave,
    onReset,
    onDirtyChange,
    onUploadingChange,
    externallyUpdated,
    onAcknowledgeExternalUpdate,
    idPrefix = 'root',
    layerName,
    onInvalid,
  },
  ref,
) {
  const rjsfRef = useRef<ParametersFormInstance>(null)
  const schemaUtils = useMemo(() => createSchemaUtils(parametersValidator, schema, PARAMETERS_DEFAULT_FORM_STATE), [schema])
  const withDefaults = useCallback(
    (data: JsonObject): JsonObject => {
      const filled = schemaUtils.getDefaultFormState(schema, data)
      return isPlainObject(filled) ? filled : data
    },
    [schemaUtils, schema],
  )

  const [trackedBaseline, setTrackedBaseline] = useState(baseline)
  const [trackedLayer, setTrackedLayer] = useState(layer)
  const [baselineSnapshot, setBaselineSnapshot] = useState<Record<string, unknown>>(baseline)
  const [initialFormData, setInitialFormData] = useState<JsonObject>(() => withDefaults(applyMergePatch(baseline, layer ?? {})))
  const [formData, setFormData] = useState<JsonObject>(initialFormData)
  const [confirmingReset, setConfirmingReset] = useState(false)
  const [generation, setGeneration] = useState(0)
  const [uploadingFieldIds, setUploadingFieldIds] = useState<Set<string>>(new Set())

  const setFieldUploading = useCallback((fieldId: string, uploading: boolean) => {
    setUploadingFieldIds((current) => {
      const next = new Set(current)
      if (uploading) {
        next.add(fieldId)
      } else {
        next.delete(fieldId)
      }
      return next
    })
  }, [])

  const dirty = Object.keys(buildMergePatch(initialFormData, formData)).length > 0
  const uploading = uploadingFieldIds.size > 0

  if ((baseline !== trackedBaseline || layer !== trackedLayer) && !dirty && !uploading) {
    setTrackedBaseline(baseline)
    setTrackedLayer(layer)
    setConfirmingReset(false)
    setBaselineSnapshot(baseline)
    const reinitialized = withDefaults(applyMergePatch(baseline, layer ?? {}))
    setInitialFormData(reinitialized)
    setFormData(reinitialized)
    setGeneration((current) => current + 1)
  }

  useEffect(() => {
    onDirtyChange(dirty || uploading)
    return () => {
      onDirtyChange(false)
    }
  }, [dirty, uploading, onDirtyChange])

  useEffect(() => {
    onUploadingChange?.(uploading)
  }, [uploading, onUploadingChange])

  const save = (): Promise<boolean> => {
    if (!dirty || saving || uploading) {
      return Promise.resolve(false)
    }
    const snapshot = formData
    const savedBaseline = baseline
    const savedLayer = layer
    return onSave(buildMergePatch(baselineSnapshot, formData)).then((success) => {
      if (success) {
        setInitialFormData(snapshot)
        setTrackedBaseline(savedBaseline)
        setTrackedLayer(savedLayer)
      }
      return success
    })
  }

  useImperativeHandle(ref, () => ({ validate: () => rjsfRef.current?.validateForm() ?? false, save }))

  const handleResetConfirm = () => {
    setConfirmingReset(false)
    const currentBaseline = baseline
    const currentLayer = layer
    void onReset().then((success) => {
      if (success) {
        const reset = withDefaults(applyMergePatch(currentBaseline, {}))
        setInitialFormData(reset)
        setFormData(reset)
        setGeneration((current) => current + 1)
        setBaselineSnapshot(currentBaseline)
        setTrackedBaseline(currentBaseline)
        setTrackedLayer(currentLayer)
      }
    })
  }

  return (
    <div className='d-flex flex-column zocks-gap-2'>
      {externallyUpdated ? (
        <Alert variant='info' dismissible onClose={onAcknowledgeExternalUpdate}>
          Values were updated elsewhere — Save will apply your edits over the new values.
        </Alert>
      ) : null}
      <ParametersRjsfForm
        idPrefix={idPrefix}
        formRef={rjsfRef}
        schema={schema}
        formData={formData}
        layer={layerName}
        setFieldUploading={setFieldUploading}
        generation={generation}
        disabled={saving}
        onChange={setFormData}
        onSubmit={() => void save()}
        onError={onInvalid}
      >
        {layer !== null ? (
          <div className='d-flex justify-content-end zocks-gap-2 pt-2'>
            {confirmingReset ? (
              <div className='d-flex align-items-center zocks-gap-2'>
                <span className='paragraph-medium'>Reset?</span>
                <Button type='button' variant='borderless' disabled={saving} onClick={() => setConfirmingReset(false)}>
                  Cancel
                </Button>
                <Button type='button' variant='danger' loading={saving} disabled={saving || uploading} onClick={handleResetConfirm}>
                  Confirm
                </Button>
              </div>
            ) : (
              <Button type='button' variant='borderless' disabled={saving || uploading} onClick={() => setConfirmingReset(true)}>
                Reset to inherited
              </Button>
            )}
          </div>
        ) : (
          <></>
        )}
      </ParametersRjsfForm>
    </div>
  )
})
